import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import LandingPage from "@/pages/LandingPage";
import JoinProviderPage from "@/pages/JoinProviderPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import BrowsePage from "@/pages/BrowsePage";
import ProviderProfilePage from "@/pages/ProviderProfilePage";
import CheckoutPage from "@/pages/CheckoutPage";
import CustomerDashboard from "@/pages/CustomerDashboard";
import ProviderDashboard from "@/pages/ProviderDashboard";
import ProviderOnboarding from "@/pages/ProviderOnboarding";
import CoordinatorPage from "@/pages/CoordinatorPage";
import MessagesPage from "@/pages/MessagesPage";
import AIPlannerPage from "@/pages/AIPlannerPage";
import AdminDashboard from "@/pages/AdminDashboard";
import TrustPage from "@/pages/TrustPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import LegalPage from "@/pages/LegalPage";
import ProfilePage from "@/pages/ProfilePage";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import InvoicePage from "@/pages/InvoicePage";
import InvoicesListPage from "@/pages/InvoicesListPage";
import InvitationsBuilderPage from "@/pages/InvitationsBuilderPage";
import InvitationsEntry from "@/pages/InvitationsEntry";
import RsvpDashboardPage from "@/pages/RsvpDashboardPage";
import PublicInvitePage from "@/pages/PublicInvitePage";
import BillingPage from "@/pages/BillingPage";
import SupportWidget from "@/components/SupportWidget";
import SupportContactModal from "@/components/SupportContactModal";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;
  // Admin can reach any role-gated route (for QA/support); others must match.
  if (roles && !roles.includes(user.role) && user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();

  // Cross-fade between routes: keep rendering the old page while it fades out,
  // then swap to the new location and fade it in.
  const [displayLocation, setDisplayLocation] = React.useState(location);
  const [stage, setStage] = React.useState('enter');

  React.useEffect(() => {
    if (location === displayLocation) return;
    if (location.pathname === displayLocation.pathname) {
      setDisplayLocation(location); // same page, new query/hash — no transition
    } else {
      setStage('exit');
    }
  }, [location, displayLocation]);

  const finishExit = React.useCallback(() => {
    setDisplayLocation(location);
    setStage('enter');
    window.scrollTo(0, 0);
  }, [location]);

  // Safety net: if the exit animation never fires its end event, force-complete
  React.useEffect(() => {
    if (stage !== 'exit') return;
    const t = setTimeout(finishExit, 400);
    return () => clearTimeout(t);
  }, [stage, finishExit]);

  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
    <div
      className={stage === 'exit' ? 'page-fade-exit' : 'page-fade-enter'}
      onAnimationEnd={(e) => { if (e.target === e.currentTarget && stage === 'exit') finishExit(); }}
    >
    <Routes location={displayLocation}>
      <Route path="/" element={<LandingPage />} />
      <Route path="/join" element={<JoinProviderPage />} />
      <Route path="/for-vendors" element={<JoinProviderPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/browse" element={<BrowsePage />} />
      <Route path="/provider/:profileId" element={<ProviderProfilePage />} />
      <Route path="/checkout/:profileId/:serviceId" element={
        <ProtectedRoute><CheckoutPage /></ProtectedRoute>
      } />
      <Route path="/checkout/success" element={
        <ProtectedRoute><CheckoutSuccess /></ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute><CustomerDashboard /></ProtectedRoute>
      } />
      <Route path="/provider/dashboard" element={
        <ProtectedRoute roles={['provider']}><ProviderDashboard /></ProtectedRoute>
      } />
      <Route path="/provider/setup-profile" element={
        <ProtectedRoute><ProviderOnboarding /></ProtectedRoute>
      } />
      {/* Legacy path — kept as a redirect so old links/bookmarks still resolve */}
      <Route path="/provider/onboarding" element={<Navigate to="/provider/setup-profile" replace />} />
      <Route path="/messages" element={
        <ProtectedRoute><MessagesPage /></ProtectedRoute>
      } />
      <Route path="/messages/:userId" element={
        <ProtectedRoute><MessagesPage /></ProtectedRoute>
      } />
      <Route path="/ai-planner" element={
        <ProtectedRoute><AIPlannerPage /></ProtectedRoute>
      } />
      <Route path="/coordinator" element={
        <ProtectedRoute><CoordinatorPage /></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>
      } />
      <Route path="/trust" element={<TrustPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/legal/:doc" element={<LegalPage />} />
      <Route path="/profile" element={
        <ProtectedRoute><ProfilePage /></ProtectedRoute>
      } />
      <Route path="/invoices" element={
        <ProtectedRoute><InvoicesListPage /></ProtectedRoute>
      } />
      <Route path="/invoices/:bookingId" element={
        <ProtectedRoute><InvoicePage /></ProtectedRoute>
      } />
      {/* Invitations (evite) */}
      <Route path="/invitations" element={
        <ProtectedRoute><InvitationsEntry /></ProtectedRoute>
      } />
      <Route path="/events/:eventId/invitations" element={
        <ProtectedRoute><InvitationsBuilderPage /></ProtectedRoute>
      } />
      <Route path="/events/:eventId/invitations/rsvps" element={
        <ProtectedRoute><RsvpDashboardPage /></ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute><BillingPage /></ProtectedRoute>
      } />
      <Route path="/i/:inviteId" element={<PublicInvitePage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </div>
    <SupportWidget />
    <SupportContactModal />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
